import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Table, Input, Select, Button, Modal, Form, Row, Col, Tag, Typography, Space,
  AutoComplete, InputNumber, Checkbox, Radio, Card, App as AntApp, Flex, Popconfirm,
  Switch, Alert, Tooltip,
} from 'antd';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../config';
import { PROPS_DINHEIRO_BR } from '../utils/dinheiroInput';
import {
  avisoUnidadePeca, casaBuscaPeca, juntarNomes, lerQuantidadePeca, pecasDisponiveis,
  pecasMudaram, reaisDeCentavos, subtotalPecaCentavos, totalCompostoCentavos,
} from '../utils/custoComposto';

const { Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

const fmt = (v: number) =>
  'R$ ' + (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Custo {
  id: string;
  codigo: string | null;
  descricao: string;
  unidade: string;
  valor_unitario: string;
  categoria: string | null;
  tag: string | null;
  fornecedor_ref: string | null;
  observacoes: string | null;
  parametros: any | null;
  patrimonios_ref: string[] | null;
  ativo: boolean;
  criado_em: string;
  atualizado_em: string;
  // Custo composto (2026-09-15): valor_unitario = soma das peças, gravada pelo servidor.
  composto?: boolean;
  componentes?: ComponenteCusto[];
  usado_em?: { id: string; descricao: string; ativo?: boolean }[];
}
interface ComponenteCusto {
  componente_id: string;
  quantidade: number | string;
  ordem: number;
  descricao: string;
  unidade: string | null;
  valor_unitario: number | string;
  ativo: boolean;
  composto: boolean;
}
interface PatrimonioBem {
  id: string;
  descricao: string;
  categoria: string | null;
  modelo: string | null;
  valor_pago: string;
  valor_residual: string;
  vida_util_meses: number;
  data_aquisicao: string;
}

// ───────── Cálculos (preservados intactos) ─────────
function calcularDiariaEquipamento(p: any): { diaria: number; detalhes: { label: string; valor: number }[]; } {
  const valor = +p.valor_aquisicao || 0;
  const residual = +p.valor_residual || 0;
  const vidaAnos = Math.max(+p.vida_util_anos || 0, 0.01);
  const depAnual = (valor - residual) / vidaAnos;
  const seguroAnual = valor * (+p.seguro_pct || 0) / 100;
  const ipvaAnual = valor * (+p.ipva_pct || 0) / 100;
  const manutAnual = valor * (+p.manutencao_periodica_pct || 0) / 100;
  const pecasAnual = valor * (+p.troca_pecas_pct || 0) / 100;
  const licenciamento = +p.licenciamento_anual || 0;
  const jurosAnualizado = (+p.juros_total || 0) / vidaAnos;
  const totalAnual = depAnual + seguroAnual + ipvaAnual + manutAnual + pecasAnual + licenciamento + jurosAnualizado;
  const freqDiasMes = Math.max(+p.frequencia_uso_dias_mes || 0, 0.01);
  let ociosidadeRaw = +p.fator_ociosidade_pct || 0;
  if (ociosidadeRaw > 1) ociosidadeRaw = ociosidadeRaw / 100;
  const ociosidade = Math.min(Math.max(ociosidadeRaw, 0), 0.95);
  const diasUsoAno = freqDiasMes * 12 * (1 - ociosidade);
  const diaria = diasUsoAno > 0 ? totalAnual / diasUsoAno : 0;
  return {
    diaria,
    detalhes: [
      { label: 'Depreciação anual', valor: depAnual },
      { label: 'Financiamento (diluído na vida útil)', valor: jurosAnualizado },
      { label: 'Licenciamento/taxas anual', valor: licenciamento },
      { label: 'Seguro anual', valor: seguroAnual },
      { label: 'IPVA/tributos anuais', valor: ipvaAnual },
      { label: 'Manutenção periódica anual', valor: manutAnual },
      { label: 'Troca de peças anual', valor: pecasAnual },
      { label: 'Total anual', valor: totalAnual },
      { label: 'Dias de uso/ano (após ociosidade)', valor: diasUsoAno },
    ],
  };
}

function calcularDiariaMaoObra(p: any): { diaria: number; detalhes: { label: string; valor: number }[]; } {
  const salario = +p.valor_contratacao || 0;
  const provFeriasMes = salario * (1/12 + (1/12)/3);
  const prov13Mes = salario * (1/12);
  const bonifMes = (+p.bonificacao_anual || 0) / 12;
  const seguroVidaMes = salario * (+p.seguro_vida_pct || 0) / 100;
  const provJurMes = salario * (+p.provisao_juridica_pct || 0) / 100;
  const custoMes = salario + provFeriasMes + prov13Mes + bonifMes + seguroVidaMes + provJurMes;
  const diasBase = Math.max(+p.dias_uteis_mes || 0, 0.01);
  const diasExtras = +p.dias_extras_mes || 0;
  const ociosidade = Math.min(Math.max(+p.fator_ociosidade_pct || 0, 0), 95) / 100;
  const diasEfetivos = (diasBase + diasExtras) * (1 - ociosidade);
  const diaria = diasEfetivos > 0 ? custoMes / diasEfetivos : 0;
  return {
    diaria,
    detalhes: [
      { label: 'Salário base', valor: salario },
      { label: 'Provisão férias + 1/3', valor: provFeriasMes },
      { label: 'Provisão 13º', valor: prov13Mes },
      { label: 'Bonificação metas (mensalizada)', valor: bonifMes },
      { label: 'Seguro de vida', valor: seguroVidaMes },
      { label: 'Provisão jurídica', valor: provJurMes },
      { label: 'Custo mensal total', valor: custoMes },
      { label: 'Dias úteis efetivos/mês', valor: diasEfetivos },
    ],
  };
}

const UNIDADES = ['Diária', 'Unidade', 'Conjunto', 'km', 'h', 'mês', 'm', 'm²', 'm³', 'kg', 'ton', 'L', 'vb', 'pct'];
const CATEGORIAS = ['Mão de Obra', 'Equipamento', 'Material', 'Serviço', 'Deslocamento', 'Software / Licença', 'Terceirizado', 'Locação', 'Outros'];
const TAGS_PADRAO = ['Topografia', 'Hidrologia', 'Gerencial', 'ART', 'Ensaio Geotécnico', 'Consultoria', 'Projeto', 'Fiscalização'];

function ComercialCustosInner() {
  const { message } = AntApp.useApp();
  const [custos, setCustos] = useState<Custo[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroCat, setFiltroCat] = useState('todas');
  const [filtroTag, setFiltroTag] = useState('todas');
  const [editando, setEditando] = useState<Custo | null>(null);
  const [mostrarNovo, setMostrarNovo] = useState(false);

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/comercial/custos`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setCustos(data);
    } finally { setLoading(false); }
  }

  async function excluir(id: string) {
    try {
      const token = await getToken();
      await axios.delete(`${API_URL}/comercial/custos/${id}`, { headers: { Authorization: token } });
      message.success('Custo desativado.');
      await carregar();
    } catch (e: any) { message.error('Não desativou: ' + mensagemErro(e), 8); }
  }

  const filtrados = custos.filter(c => {
    if (filtroCat !== 'todas' && c.categoria !== filtroCat) return false;
    if (filtroTag !== 'todas' && c.tag !== filtroTag) return false;
    if (!busca) return true;
    const q = busca.toLowerCase();
    return c.descricao.toLowerCase().includes(q)
            || (c.codigo || '').toLowerCase().includes(q)
            || (c.tag || '').toLowerCase().includes(q)
            || (c.fornecedor_ref || '').toLowerCase().includes(q);
  });

  const categoriasExistentes = Array.from(new Set(custos.map(c => c.categoria).filter(Boolean))).sort() as string[];
  const tagsExistentes = Array.from(new Set(custos.map(c => c.tag).filter(Boolean))).sort() as string[];

  const cols: ColumnsType<Custo> = [
    {
      title: 'Código', dataIndex: 'codigo', key: 'cod', width: 110,
      render: (v) => v ? <Text style={{ fontFamily: 'monospace', color: '#6A7092', fontSize: 11 }}>{v}</Text> : '—',
    },
    {
      title: 'Descrição', dataIndex: 'descricao', key: 'desc',
      render: (v, r) => {
        const usadoEm = (r.usado_em || []).filter(u => u.ativo !== false);
        return (
          <div>
            <Space size={6} wrap>
              <Text strong style={{ color: '#020D7F', fontSize: 12 }}>{v}</Text>
              {r.composto && (
                <Tooltip title={(r.componentes || []).map(p => `${fmtQtd(Number(p.quantidade))} × ${p.descricao}`).join(' · ') || 'sem peças'}>
                  <Tag color="purple">Composto · {(r.componentes || []).length} {(r.componentes || []).length === 1 ? 'peça' : 'peças'}</Tag>
                </Tooltip>
              )}
            </Space>
            {usadoEm.length > 0 && (
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                peça de: {usadoEm.map(u => u.descricao).join(', ')}
              </Text>
            )}
          </div>
        );
      },
    },
    { title: 'Categoria', dataIndex: 'categoria', key: 'cat', width: 140, render: (v) => v || '—' },
    {
      title: 'Tag', dataIndex: 'tag', key: 'tag', width: 130,
      render: (v) => v ? <Tag color="blue">{v}</Tag> : '—',
    },
    { title: 'Unidade', dataIndex: 'unidade', key: 'un', width: 90 },
    {
      title: 'Valor unit.', dataIndex: 'valor_unitario', key: 'vu', width: 120, align: 'right',
      render: (v) => fmt(parseFloat(v)),
    },
    { title: 'Fornecedor', dataIndex: 'fornecedor_ref', key: 'fr', render: (v) => v || '—' },
    {
      title: '', key: 'ac', width: 110, align: 'right',
      render: (_, r) => (
        <Space size={2}>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditando(r)} />
          <Popconfirm
            title="Desativar este custo?"
            description="Ele continuará referenciado por composições existentes mas não aparecerá mais no catálogo."
            onConfirm={() => excluir(r.id)} okText="Desativar" cancelText="Cancelar" okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1300, margin: '16px auto', padding: '0 24px' }}>
      <Flex gap={12} align="center" wrap="wrap" style={{ marginBottom: 14 }}>
        <Typography.Title level={4} style={{ margin: 0, color: '#020D7F' }}>Custos Unitários</Typography.Title>
        <Select value={filtroCat} onChange={setFiltroCat} size="small" style={{ width: 160 }}
          options={[
            { label: 'Todas categorias', value: 'todas' },
            ...categoriasExistentes.map(c => ({ label: c, value: c })),
          ]} />
        <Select value={filtroTag} onChange={setFiltroTag} size="small" style={{ width: 160 }}
          options={[
            { label: 'Todas as tags', value: 'todas' },
            ...tagsExistentes.map(t => ({ label: t, value: t })),
          ]} />
        <Input
          placeholder="Buscar código, descrição, fornecedor..."
          value={busca} onChange={e => setBusca(e.target.value)}
          prefix={<SearchOutlined style={{ color: '#aaa' }} />}
          allowClear style={{ maxWidth: 320, flex: 1 }}
        />
        <Button type="primary" icon={<PlusOutlined />} style={{ marginLeft: 'auto' }} onClick={() => setMostrarNovo(true)}>
          Novo custo
        </Button>
      </Flex>

      <Table
        rowKey="id" columns={cols} dataSource={filtrados} loading={loading}
        size="small" pagination={{ pageSize: 50, showSizeChanger: true }}
        locale={{ emptyText: 'Nenhum custo cadastrado.' }}
        scroll={{ x: 'max-content' }}
      />

      {(editando || mostrarNovo) && (
        <FormCusto custo={editando} catalogo={custos}
          onClose={() => { setEditando(null); setMostrarNovo(false); }}
          onSaved={async () => { await carregar(); setEditando(null); setMostrarNovo(false); }} />
      )}
    </div>
  );
}

interface LinhaPeca {
  componente_id: string;
  quantidade: number | null;
  descricao: string;
  unidade: string | null;
  valor_unitario: number;
  ativo: boolean;
  composto: boolean;
}

function lerResposta(res: any): any {
  const data = res?.data;
  if (data && typeof data.body === 'string') {
    try { return JSON.parse(data.body); } catch { return data; }
  }
  return data;
}

// O servidor devolve { error } (hydrodata-shared/http.js); a tela lia `.erro` e
// mostrava só "Request failed with status code 400".
function mensagemErro(e: any): string {
  const data = lerResposta(e?.response);
  return data?.error || data?.erro || e?.message || 'erro desconhecido';
}

const fmtQtd = (q: number | null) =>
  q === null ? '—' : q.toLocaleString('pt-BR', { maximumFractionDigits: 4 });

function FormCusto({ custo, catalogo, onClose, onSaved }: {
  custo: Custo | null; catalogo: Custo[]; onClose: () => void; onSaved: () => void;
}) {
  const { message, notification } = AntApp.useApp();
  const [form, setForm] = useState<any>({
    codigo: custo?.codigo || '',
    descricao: custo?.descricao || '',
    unidade: custo?.unidade || 'Diária',
    valor_unitario: custo?.valor_unitario || '',
    categoria: custo?.categoria || 'Mão de Obra',
    tag: custo?.tag || '',
    fornecedor_ref: custo?.fornecedor_ref || '',
    observacoes: custo?.observacoes || '',
    parametros: custo?.parametros || null,
    patrimonios_ref: (custo?.patrimonios_ref || []) as string[],
  });
  const [salvando, setSalvando] = useState(false);
  const [mostrarCalc, setMostrarCalc] = useState(!!custo?.parametros);
  const [bens, setBens] = useState<PatrimonioBem[]>([]);
  const [composto, setComposto] = useState<boolean>(!!custo?.composto);
  const [pecas, setPecas] = useState<LinhaPeca[]>(() => (custo?.componentes || []).map(p => ({
    componente_id: p.componente_id,
    quantidade: Number(p.quantidade),
    descricao: p.descricao,
    unidade: p.unidade,
    valor_unitario: Number(p.valor_unitario) || 0,
    ativo: p.ativo !== false,
    composto: !!p.composto,
  })));

  useEffect(() => {
    if (form.categoria !== 'Equipamento' || composto) return;
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API_URL}/patrimonio`, { headers: { Authorization: token } });
        const data = res.data.body ? JSON.parse(res.data.body) : res.data;
        setBens(Array.isArray(data) ? data : []);
      } catch {}
    })();
  }, [form.categoria, composto]);

  const podeCalcular = !composto && (form.categoria === 'Mão de Obra' || form.categoria === 'Equipamento');
  const calcResult = podeCalcular && mostrarCalc && form.parametros
    ? (form.categoria === 'Mão de Obra'
        ? calcularDiariaMaoObra(form.parametros)
        : calcularDiariaEquipamento(form.parametros))
    : null;

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const setParam = (k: string, v: any) => setForm((f: any) => ({ ...f, parametros: { ...(f.parametros || {}), [k]: v } }));

  const totalCentavos = totalCompostoCentavos(pecas.map(p => ({ quantidade: p.quantidade ?? 0, valor_unitario: p.valor_unitario })));
  const opcoesPeca = pecasDisponiveis(catalogo, custo?.id, pecas.map(p => p.componente_id));
  const porId = new Map(catalogo.map(c => [c.id, c]));
  const mensais = pecas.filter(p => avisoUnidadePeca(p.unidade) === 'mensal');
  const desativadas = pecas.filter(p => !p.ativo);
  const usadoEmAtivos = (custo?.usado_em || []).filter(u => u.ativo !== false);

  function ligarComposto(ligar: boolean) {
    setComposto(ligar);
    if (ligar && !custo && form.unidade === 'Diária') set('unidade', 'Unidade');
  }

  function adicionarPeca(id: string) {
    const c = porId.get(id);
    if (!c) return;
    setPecas(lista => [...lista, {
      componente_id: c.id, quantidade: 1, descricao: c.descricao, unidade: c.unidade,
      valor_unitario: Number(c.valor_unitario) || 0, ativo: c.ativo !== false, composto: !!c.composto,
    }]);
  }
  const mudarQuantidade = (id: string, q: number | null) =>
    setPecas(lista => lista.map(p => p.componente_id === id ? { ...p, quantidade: q } : p));
  const tirarPeca = (id: string) => setPecas(lista => lista.filter(p => p.componente_id !== id));

  function aplicarValor() {
    if (!calcResult) return;
    setForm((f: any) => ({ ...f, valor_unitario: calcResult.diaria.toFixed(2) }));
  }

  function recalcularDoPatrimonio(refs: string[]) {
    const selecionados = bens.filter(b => refs.includes(b.id));
    if (selecionados.length === 0) {
      setForm((f: any) => ({ ...f, patrimonios_ref: [] }));
      return;
    }
    const aquisicao = selecionados.reduce((s, b) => s + (parseFloat(b.valor_pago) || 0), 0);
    const residual = selecionados.reduce((s, b) => s + (parseFloat(b.valor_residual) || 0), 0);
    const vidaMeses = Math.min(...selecionados.map(b => b.vida_util_meses || 60));
    const descricaoSugerida = selecionados.map(b => b.descricao + (b.modelo ? ` (${b.modelo})` : '')).join(' + ');
    setForm((f: any) => ({
      ...f, patrimonios_ref: refs,
      descricao: f.descricao || descricaoSugerida,
      parametros: {
        ...(f.parametros || {}),
        valor_aquisicao: aquisicao, valor_residual: residual,
        vida_util_anos: Math.max(Math.round(vidaMeses / 12), 1),
      },
    }));
    setMostrarCalc(true);
  }
  function togglePatrimonio(bemId: string) {
    const refs = form.patrimonios_ref || [];
    recalcularDoPatrimonio(refs.includes(bemId) ? refs.filter((x: string) => x !== bemId) : [...refs, bemId]);
  }

  async function salvar() {
    if (!form.descricao) { message.warning('Descrição obrigatória'); return; }
    if (composto && pecas.length === 0) { message.warning('Adicione pelo menos uma peça ao custo composto.'); return; }
    if (composto && mensais.length) {
      message.warning(`Tire ${juntarNomes(mensais.map(p => p.descricao))} da lista: mensalidade não entra no custo composto.`, 6);
      return;
    }
    if (composto && pecas.some(p => lerQuantidadePeca(p.quantidade) === null)) { message.warning('Toda peça precisa de quantidade maior que zero.'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const payload: any = {
        ...form,
        valor_unitario: parseFloat(form.valor_unitario) || 0,
        patrimonios_ref: (form.patrimonios_ref || []).length ? form.patrimonios_ref : null,
        parametros: mostrarCalc ? form.parametros : null,
      };
      // Na edição, `composto` e `componentes` só vão quando a pessoa mexeu neles. Uma aba
      // aberta antes de alguém converter o custo (ou trocar as peças) não pode desfazer
      // isso só por corrigir a observação — o servidor recusa o valor velho com recado.
      const mexeuNaChave = !custo || composto !== !!custo.composto;
      if (mexeuNaChave) payload.composto = composto;
      if (composto) {
        // O valor é a soma das peças (o servidor calcula); a calculadora não se aplica.
        delete payload.valor_unitario;
        delete payload.parametros;
        if (mexeuNaChave || pecasMudaram(custo?.componentes || [], pecas)) {
          payload.componentes = pecas.map(p => ({ componente_id: p.componente_id, quantidade: lerQuantidadePeca(p.quantidade) }));
        }
      }
      const res = custo
        ? await axios.put(`${API_URL}/comercial/custos/${custo.id}`, payload, { headers: { Authorization: token } })
        : await axios.post(`${API_URL}/comercial/custos`, payload, { headers: { Authorization: token } });
      const salvo = lerResposta(res);
      // Servidor ainda sem a função (portal publicado antes do comercial-engine):
      // ele ignora as peças e grava o custo como simples. Não dizer "salvo".
      if ((payload.composto !== undefined || payload.componentes) && !Array.isArray(salvo?.compostos_recalculados)) {
        message.error('O servidor ainda está sendo atualizado e não guardou as peças. Espere uns minutos, recarregue a página e confira este custo.', 10);
        onSaved();
        return;
      }
      message.success(composto ? `Custo salvo — ${reaisDeCentavos(Math.round(Number(salvo?.valor_unitario) * 100))}.` : 'Custo salvo.');
      const recalculados: { descricao: string; de: number; para: number }[] = salvo?.compostos_recalculados || [];
      if (recalculados.length) {
        notification.info({
          title: recalculados.length === 1 ? 'Isto também mudou um custo composto' : `Isto também mudou ${recalculados.length} custos compostos`,
          description: (
            <div>
              {recalculados.map(r => (
                <div key={r.descricao}>
                  <b>{r.descricao}</b>: {fmt(r.de)} → {fmt(r.para)}
                </div>
              ))}
              <Text type="secondary" style={{ fontSize: 11 }}>Orçamentos já montados continuam com o valor antigo.</Text>
            </div>
          ),
          duration: 15,
        });
      }
      onSaved();
    } catch (e: any) { message.error('Não salvou: ' + mensagemErro(e), 8); }
    finally { setSalvando(false); }
  }

  const colunasPecas: ColumnsType<LinhaPeca> = [
    {
      title: 'Peça', dataIndex: 'descricao', key: 'd',
      render: (v, p) => {
        const aviso = avisoUnidadePeca(p.unidade);
        return (
          <Space size={4} wrap>
            <Text style={{ fontSize: 12 }}>{v}</Text>
            {p.composto && <Tag color="purple">composto</Tag>}
            {!p.ativo && <Tag color="red">desativada no catálogo</Tag>}
            {aviso === 'mensal' && <Tag color="red">por mês — tire daqui</Tag>}
            {aviso === 'diaria' && (
              <Tooltip title="Serve para a diária de quem instala. Se for aluguel ou uso do equipamento, deixe fora.">
                <Tag color="gold">diária — confira</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    { title: 'Unidade', dataIndex: 'unidade', key: 'u', width: 90, render: v => v || '—' },
    {
      title: 'Quantidade', key: 'q', width: 120,
      render: (_, p) => (
        <InputNumber {...PROPS_DINHEIRO_BR} step={1} min={0} size="small" style={{ width: '100%' }}
          status={lerQuantidadePeca(p.quantidade) === null ? 'error' : undefined}
          value={p.quantidade ?? undefined} onChange={v => mudarQuantidade(p.componente_id, v as number | null)} />
      ),
    },
    { title: 'Valor unit.', dataIndex: 'valor_unitario', key: 'v', width: 120, align: 'right', render: v => fmt(v) },
    {
      title: 'Subtotal', key: 's', width: 130, align: 'right',
      render: (_, p) => reaisDeCentavos(subtotalPecaCentavos({ quantidade: p.quantidade ?? 0, valor_unitario: p.valor_unitario })),
    },
    {
      title: '', key: 'x', width: 44, align: 'right',
      render: (_, p) => <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label={`Tirar ${p.descricao}`} onClick={() => tirarPeca(p.componente_id)} />,
    },
  ];

  return (
    <Modal
      open onCancel={onClose} onOk={salvar} confirmLoading={salvando}
      width="94vw" style={{ top: 16, maxWidth: 1100 }}
      styles={{ body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto', overflowX: 'hidden' } }}
      title={custo ? 'Editar custo' : 'Novo custo unitário'}
      okText={custo ? 'Salvar' : 'Criar'} cancelText="Cancelar" destroyOnHidden
    >
      <Form layout="vertical" size="small">
        {usadoEmAtivos.length > 0 && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            title={`Este custo é peça de ${juntarNomes(usadoEmAtivos.map(u => u.descricao))}.`}
            description={`Mudar o valor aqui muda o valor ${usadoEmAtivos.length > 1 ? 'deles' : 'dele'} também, na hora. Orçamentos já montados não mudam.`} />
        )}
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Código interno">
              <Input value={form.codigo} onChange={e => set('codigo', e.target.value)} placeholder="Ex.: MO-TOP-01" />
            </Form.Item>
          </Col>
          <Col xs={24} md={16}>
            <Form.Item label="Descrição" required>
              <Input value={form.descricao} onChange={e => set('descricao', e.target.value)}
                placeholder={composto ? 'Ex.: Estação hidrológica telemétrica' : 'Ex.: Topógrafo em campo'} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Categoria">
              <Select value={form.categoria} onChange={(v) => set('categoria', v)}
                options={CATEGORIAS.map(c => ({ label: c, value: c }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Tag (disciplina/área)">
              <AutoComplete value={form.tag} onChange={(v) => set('tag', v)} placeholder="Ex.: Topografia"
                options={TAGS_PADRAO.map(t => ({ value: t }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Fornecedor referência">
              <Input value={form.fornecedor_ref} onChange={e => set('fornecedor_ref', e.target.value)} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Unidade">
              <Select value={form.unidade} onChange={(v) => set('unidade', v)}
                options={UNIDADES.map(u => ({ label: u, value: u }))} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            {composto ? (
              <Form.Item label="Valor unitário (R$)" extra="Soma das peças — o valor final é calculado ao salvar.">
                <Input value={reaisDeCentavos(totalCentavos)} readOnly style={{ fontWeight: 600, color: '#020D7F' }} />
              </Form.Item>
            ) : (
              <Form.Item label="Valor unitário (R$)" required>
                <InputNumber {...PROPS_DINHEIRO_BR} value={parseFloat(form.valor_unitario) || 0}
                  onChange={(v) => set('valor_unitario', String(v ?? 0))} min={0} style={{ width: '100%' }} />
              </Form.Item>
            )}
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Custo composto" extra="Soma de peças do catálogo (ex.: estação = painel + bateria + terminal).">
              <Switch checked={composto} onChange={ligarComposto} checkedChildren="soma de peças" unCheckedChildren="valor digitado" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Observações">
              <Input.TextArea value={form.observacoes} onChange={e => set('observacoes', e.target.value)} rows={2} />
            </Form.Item>
          </Col>
        </Row>

        {!composto && custo?.composto && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            title="Ao salvar, as peças saem e o valor volta a ser o digitado." />
        )}

        {composto && (
          <Card size="small" style={{ background: '#FAFBFF', borderColor: '#CDD4EA', marginBottom: 12 }}
            title={<Text strong style={{ color: '#020D7F' }}>Peças ({pecas.length}) — total {reaisDeCentavos(totalCentavos)}</Text>}>
            <Select
              value={null as unknown as string}
              placeholder="Buscar peça no catálogo: painel, bateria, ST6100…"
              style={{ width: '100%', marginBottom: 10 }}
              showSearch={{ filterOption: (input, option) => {
                const c = option ? porId.get(String(option.value)) : undefined;
                return !!c && casaBuscaPeca(c, input);
              } }}
              onChange={adicionarPeca}
              notFoundContent="Nenhum custo encontrado. Cadastre a peça como custo unitário primeiro."
              options={opcoesPeca.map(c => ({
                value: c.id,
                label: `${c.descricao} · ${c.unidade || '—'} · ${fmt(parseFloat(String(c.valor_unitario)))}${c.composto ? ' · composto' : ''}`,
              }))}
            />
            {mensais.length > 0 && (
              <Alert type="error" showIcon style={{ marginBottom: 10 }}
                title={`${juntarNomes(mensais.map(p => p.descricao))} ${mensais.length > 1 ? 'são cobrados' : 'é cobrado'} por mês e não pode ficar aqui.`}
                description="O custo composto soma só compra e instalação. Tire da lista e lance a mensalidade (ex.: plano satelital) como item separado na composição, com a quantidade de meses. Enquanto estiver na lista, não salva." />
            )}
            {desativadas.length > 0 && (
              <Alert type="warning" showIcon style={{ marginBottom: 10 }}
                title={`${juntarNomes(desativadas.map(p => p.descricao))} ${desativadas.length > 1 ? 'estão desativadas' : 'está desativada'} no catálogo.`}
                description="Reative a peça no catálogo ou tire da lista — senão o custo composto não salva." />
            )}
            <Table rowKey="componente_id" size="small" pagination={false} columns={colunasPecas} dataSource={pecas}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'Nenhuma peça ainda. Busque acima e escolha as peças do custo.' }} />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
              Quando o preço de uma peça muda no catálogo, este valor muda junto. Orçamentos já montados continuam com o valor antigo.
            </Text>
          </Card>
        )}

        {podeCalcular && (
          <Card size="small" style={{ background: '#FAFBFF', borderColor: '#CDD4EA' }} bodyStyle={{ padding: 12 }}>
            <Flex align="center" gap={10} style={{ marginBottom: 10 }}>
              <Checkbox checked={mostrarCalc} onChange={e => setMostrarCalc(e.target.checked)}>
                <Text strong style={{ color: '#020D7F' }}>Calcular a partir de parâmetros estruturados</Text>
              </Checkbox>
              {mostrarCalc && calcResult && (
                <Button type="default" size="small" style={{ marginLeft: 'auto', color: '#0a7f4a', borderColor: '#0a7f4a' }}
                  onClick={aplicarValor}>
                  ↓ Aplicar R$ {calcResult.diaria.toFixed(2)} ao valor unitário
                </Button>
              )}
            </Flex>

            {mostrarCalc && form.categoria === 'Equipamento' && (
              <CalcEquipamento params={form.parametros || {}} setParam={setParam}
                bens={bens} patrimoniosRef={form.patrimonios_ref || []} onTogglePatr={togglePatrimonio} />
            )}
            {mostrarCalc && form.categoria === 'Mão de Obra' && (
              <CalcGridMaoObra params={form.parametros || {}} setParam={setParam} />
            )}

            {calcResult && (
              <ResultadoCalc result={calcResult} unidade={form.unidade} categoria={form.categoria} params={form.parametros || {}} />
            )}
          </Card>
        )}
      </Form>
    </Modal>
  );
}

function CampoNumero({ label, valor, onChange, sufixo, dica }: {
  label: string; valor: any; onChange: (v: number) => void; sufixo?: string; dica?: string;
}) {
  return (
    <Form.Item label={`${label}${sufixo ? ` (${sufixo})` : ''}`} extra={dica} style={{ marginBottom: 8 }}>
      <InputNumber value={valor ?? 0} onChange={(v) => onChange(Number(v) || 0)}
        step={0.01} style={{ width: '100%' }} />
    </Form.Item>
  );
}

function SeletorPatrimonio({ bens, patrimoniosRef, onToggle }: {
  bens: PatrimonioBem[]; patrimoniosRef: string[]; onToggle: (id: string) => void;
}) {
  const [busca, setBusca] = useState('');
  const [filtroCat, setFiltroCat] = useState('todas');
  const [soSelecionados, setSoSelecionados] = useState(false);

  const categoriasBens = Array.from(new Set(bens.map(b => b.categoria).filter(Boolean))).sort() as string[];
  const filtrados = bens.filter(b => {
    if (soSelecionados && !patrimoniosRef.includes(b.id)) return false;
    if (filtroCat !== 'todas' && b.categoria !== filtroCat) return false;
    if (!busca) return true;
    const q = busca.toLowerCase();
    return b.descricao.toLowerCase().includes(q)
        || (b.modelo || '').toLowerCase().includes(q)
        || (b.categoria || '').toLowerCase().includes(q);
  });

  const totalSelecionado = bens.filter(b => patrimoniosRef.includes(b.id))
    .reduce((s, b) => s + (parseFloat(b.valor_pago) || 0), 0);

  return (
    <Card size="small" style={{ marginBottom: 10 }} bodyStyle={{ padding: 10 }}>
      <Text strong style={{ fontSize: 11, color: '#555', display: 'block', marginBottom: 8 }}>
        Bens do Patrimônio que compõem este custo (opcional)
      </Text>
      <Flex gap={6} align="center" wrap="wrap" style={{ marginBottom: 8 }}>
        <Input value={busca} onChange={e => setBusca(e.target.value)}
          placeholder="Buscar por nome, modelo ou categoria..."
          prefix={<SearchOutlined style={{ color: '#aaa' }} />} size="small"
          style={{ flex: 1, minWidth: 180 }} allowClear />
        <Select value={filtroCat} onChange={setFiltroCat} size="small" style={{ width: 160 }}
          options={[{ label: 'Todas categorias', value: 'todas' }, ...categoriasBens.map(c => ({ label: c, value: c }))]} />
        <Checkbox checked={soSelecionados} onChange={e => setSoSelecionados(e.target.checked)}>
          <Text style={{ fontSize: 11 }}>Só selecionados</Text>
        </Checkbox>
        <Text type="secondary" style={{ fontSize: 10, marginLeft: 'auto' }}>
          {filtrados.length} de {bens.length}
        </Text>
      </Flex>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 130, overflowY: 'auto', padding: 2 }}>
        {filtrados.map(b => (
          <Tag.CheckableTag
            key={b.id} checked={patrimoniosRef.includes(b.id)}
            onChange={() => onToggle(b.id)}
            style={{ padding: '4px 8px' }}
          >
            {b.descricao}{b.modelo ? ` (${b.modelo})` : ''} — R$ {parseFloat(b.valor_pago).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}
          </Tag.CheckableTag>
        ))}
        {filtrados.length === 0 && (
          <Text type="secondary" italic style={{ fontSize: 11, padding: 8 }}>Nenhum bem encontrado.</Text>
        )}
      </div>
      {patrimoniosRef.length > 0 && (
        <Text style={{ fontSize: 10, color: '#0a7f4a', marginTop: 8, fontWeight: 500, display: 'block' }}>
          ✓ {patrimoniosRef.length} bem(ns) selecionado(s) — total R$ {totalSelecionado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (somado em "valor de aquisição")
        </Text>
      )}
    </Card>
  );
}

function Secao({ titulo, dica, children }: { titulo: string; dica?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 12 }}>
      <Text strong style={{ fontSize: 11, letterSpacing: '0.04em', color: '#0930D0', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
        {titulo}
      </Text>
      {dica && <Text type="secondary" italic style={{ fontSize: 10, display: 'block', marginBottom: 6 }}>{dica}</Text>}
      <Row gutter={[10, 4]}>{children}</Row>
    </div>
  );
}

function CalcEquipamento({ params, setParam, bens, patrimoniosRef, onTogglePatr }: {
  params: any; setParam: (k: string, v: any) => void;
  bens: PatrimonioBem[]; patrimoniosRef: string[]; onTogglePatr: (id: string) => void;
}) {
  const tipo = params.tipo_bem || 'equipamento';
  const isVeiculo = tipo === 'veiculo';
  return (
    <div>
      <Flex gap={16} align="center" style={{ marginBottom: 8 }}>
        <Text strong style={{ fontSize: 11, color: '#555' }}>Tipo do bem:</Text>
        <Radio.Group value={tipo} onChange={(e) => setParam('tipo_bem', e.target.value)}>
          <Radio value="equipamento">Equipamento</Radio>
          <Radio value="veiculo">Veículo</Radio>
        </Radio.Group>
      </Flex>

      {bens.length > 0 && (
        <SeletorPatrimonio bens={bens} patrimoniosRef={patrimoniosRef} onToggle={onTogglePatr} />
      )}

      <Secao titulo="Aquisição, financiamento, licenciamento e depreciação"
        dica={isVeiculo ? 'Inclui taxas anuais de licenciamento e custos do financiamento.' : 'Para equipamentos sem licenciamento, deixe em branco.'}>
        <Col span={8}><CampoNumero label="Valor de aquisição" sufixo="R$" valor={params.valor_aquisicao} onChange={v => setParam('valor_aquisicao', v)} /></Col>
        <Col span={8}><CampoNumero label="Valor residual estimado" sufixo="R$" valor={params.valor_residual} onChange={v => setParam('valor_residual', v)} dica="Valor de revenda após vida útil" /></Col>
        <Col span={8}><CampoNumero label="Vida útil econômica" sufixo="anos" valor={params.vida_util_anos} onChange={v => setParam('vida_util_anos', v)} /></Col>
        <Col span={8}><CampoNumero label="Custo total do financiamento" sufixo="R$" valor={params.juros_total} onChange={v => setParam('juros_total', v)} dica="Soma de juros + tarifas" /></Col>
        {isVeiculo && <Col span={8}><CampoNumero label="Licenciamento / taxas" sufixo="R$/ano" valor={params.licenciamento_anual} onChange={v => setParam('licenciamento_anual', v)} /></Col>}
      </Secao>

      <Secao titulo="Estimativa de uso e ciclo de manutenção"
        dica="Determina quantos dias o bem trabalha por ano.">
        <Col span={8}><CampoNumero label="Frequência de uso" sufixo="dias/mês" valor={params.frequencia_uso_dias_mes} onChange={v => setParam('frequencia_uso_dias_mes', v)} dica="Dias por mês em que o bem é mobilizado" /></Col>
        <Col span={8}><CampoNumero label="Fator de ociosidade" sufixo="0–1" valor={params.fator_ociosidade_pct} onChange={v => setParam('fator_ociosidade_pct', v)} dica="Fração de dias não faturáveis. Ex.: 0.3 = 30%" /></Col>
        {isVeiculo && (
          <>
            <Col span={8}><CampoNumero label="Uso estimado" sufixo="km/ano" valor={params.uso_estimado_km_ano} onChange={v => setParam('uso_estimado_km_ano', v)} /></Col>
            <Col span={8}><CampoNumero label="Ciclo de manutenção" sufixo="km" valor={params.ciclo_manutencao_km} onChange={v => setParam('ciclo_manutencao_km', v)} /></Col>
          </>
        )}
      </Secao>

      <Secao titulo="Encargos fixos" dica="Custos recorrentes proporcionais ao valor do bem.">
        <Col span={8}><CampoNumero label="Seguro" sufixo="% do bem/ano" valor={params.seguro_pct} onChange={v => setParam('seguro_pct', v)} /></Col>
        {isVeiculo && <Col span={8}><CampoNumero label="IPVA / tributos" sufixo="% do bem/ano" valor={params.ipva_pct} onChange={v => setParam('ipva_pct', v)} /></Col>}
      </Secao>

      <Secao titulo="Manutenção e desgaste" dica="Estimativa anual de manutenção corretiva, peças e revisões.">
        <Col span={8}><CampoNumero label="Manutenção periódica" sufixo="% do bem/ano" valor={params.manutencao_periodica_pct} onChange={v => setParam('manutencao_periodica_pct', v)} dica="Revisões programadas" /></Col>
        <Col span={8}><CampoNumero label="Troca de peças" sufixo="% do bem/ano" valor={params.troca_pecas_pct} onChange={v => setParam('troca_pecas_pct', v)} dica="Itens de desgaste" /></Col>
      </Secao>
    </div>
  );
}

function ResultadoCalc({ result, unidade, categoria, params }: {
  result: { diaria: number; detalhes: { label: string; valor: number }[] };
  unidade: string; categoria: string; params: any;
}) {
  const isEquip = categoria === 'Equipamento';
  const totalIdx = result.detalhes.findIndex(d => d.label.toLowerCase().includes('total'));
  const componentes = totalIdx > 0 ? result.detalhes.slice(0, totalIdx) : result.detalhes;
  const totalAnual = totalIdx > 0 ? result.detalhes[totalIdx].valor : componentes.reduce((s, d) => s + d.valor, 0);
  const totalRef = isEquip ? totalAnual : (params.valor_contratacao ? result.detalhes.find(d => d.label.includes('mensal'))?.valor || 0 : 0);

  const fmtBR = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const freqDiasMes = +params.frequencia_uso_dias_mes || 0;
  const ociosidade = Math.min(Math.max(+params.fator_ociosidade_pct || 0, 0), 0.95);
  const diasMesEfetivos = isEquip
    ? freqDiasMes * (1 - ociosidade)
    : ((+params.dias_uteis_mes || 0) + (+params.dias_extras_mes || 0)) * (1 - ociosidade);
  const custoMes = isEquip ? totalAnual / 12 : (result.detalhes.find(d => d.label.includes('mensal'))?.valor || 0);
  const custoAno = isEquip ? totalAnual : custoMes * 12;

  return (
    <Card size="small" style={{ marginTop: 14, borderColor: '#CDD4EA' }} bodyStyle={{ padding: 12 }}>
      <Row gutter={10} style={{ marginBottom: 12 }}>
        <Col span={6}><KpiInt label={`Custo / ${unidade.toLowerCase()}`} valor={`R$ ${fmtBR(result.diaria)}`} cor="#0a7f4a" destaque /></Col>
        <Col span={6}><KpiInt label="Custo / mês" valor={`R$ ${fmtBR(custoMes)}`} cor="#020D7F" /></Col>
        <Col span={6}><KpiInt label="Custo / ano" valor={`R$ ${fmtBR(custoAno)}`} cor="#020D7F" /></Col>
        <Col span={6}><KpiInt label={isEquip ? 'Dias úteis/mês' : 'Dias efetivos/mês'} valor={diasMesEfetivos.toFixed(1)} cor="#6A7092" /></Col>
      </Row>

      {totalRef > 0 && componentes.length > 0 && (
        <div>
          <Text strong style={{ fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }}>
            Composição do custo {isEquip ? 'anual' : 'mensal'}
          </Text>
          {componentes.filter(c => c.valor > 0.01).map(c => {
            const pct = (c.valor / totalRef) * 100;
            return (
              <Flex key={c.label} align="center" gap={8} style={{ fontSize: 11, marginBottom: 3 }}>
                <div style={{ flex: '0 0 200px', color: '#555' }}>{c.label}</div>
                <div style={{ flex: 1, height: 8, background: '#F0F1F5', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: '#0930D0' }} />
                </div>
                <div style={{ flex: '0 0 80px', textAlign: 'right', fontFamily: 'monospace', color: '#333' }}>R$ {fmtBR(c.valor)}</div>
                <div style={{ flex: '0 0 50px', textAlign: 'right', fontFamily: 'monospace', color: '#888' }}>{pct.toFixed(1)}%</div>
              </Flex>
            );
          })}
        </div>
      )}

      <Text type="secondary" italic style={{ fontSize: 10, display: 'block', marginTop: 10, lineHeight: 1.4 }}>
        ℹ Ociosidade aplica-se apenas ao denominador (dias úteis efetivos), não aos custos fixos.
      </Text>
    </Card>
  );
}

function KpiInt({ label, valor, cor, destaque }: { label: string; valor: string; cor: string; destaque?: boolean }) {
  return (
    <Card size="small" bodyStyle={{ padding: 8 }}
      style={{ background: destaque ? `${cor}15` : '#FAFBFF', borderColor: destaque ? cor : '#e0e0e0' }}>
      <Text type="secondary" style={{ fontSize: 10 }}>{label}</Text>
      <div style={{ fontSize: destaque ? 16 : 13, color: cor, fontWeight: 600, marginTop: 2 }}>{valor}</div>
    </Card>
  );
}

function CalcGridMaoObra({ params, setParam }: { params: any; setParam: (k: string, v: any) => void }) {
  return (
    <Row gutter={[10, 4]}>
      <Col span={8}><CampoNumero label="Valor de contratação mensal" sufixo="R$" valor={params.valor_contratacao} onChange={v => setParam('valor_contratacao', v)} dica="Salário base ou pró-labore bruto" /></Col>
      <Col span={8}><CampoNumero label="Bonificação por metas" sufixo="R$/ano" valor={params.bonificacao_anual} onChange={v => setParam('bonificacao_anual', v)} dica="Total pago em bônus no ano" /></Col>
      <Col span={8}><CampoNumero label="Seguro de vida" sufixo="% do salário" valor={params.seguro_vida_pct} onChange={v => setParam('seguro_vida_pct', v)} /></Col>
      <Col span={8}><CampoNumero label="Provisão jurídica" sufixo="% do salário" valor={params.provisao_juridica_pct} onChange={v => setParam('provisao_juridica_pct', v)} dica="Reserva para passivos trabalhistas" /></Col>
      <Col span={8}><CampoNumero label="Dias úteis de trabalho" sufixo="dias/mês" valor={params.dias_uteis_mes} onChange={v => setParam('dias_uteis_mes', v)} dica="Média (20–22)" /></Col>
      <Col span={8}><CampoNumero label="Dias extras" sufixo="dias/mês" valor={params.dias_extras_mes} onChange={v => setParam('dias_extras_mes', v)} dica="Média mensal de horas extras" /></Col>
      <Col span={8}><CampoNumero label="Fator de ociosidade" sufixo="%" valor={params.fator_ociosidade_pct} onChange={v => setParam('fator_ociosidade_pct', v)} dica="% de dias não faturáveis (0–95)" /></Col>
    </Row>
  );
}

export default function ComercialCustos() {
  return (
    <AntApp>
      <ComercialCustosInner />
    </AntApp>
  );
}
